import React, { useState, useEffect } from 'react';
import { X, Book, Video, Plus, Check, User, Calendar, Volume2, Crown, BookOpen } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import API_URL from '../config/API_URL';
export const AddStoriesModal = ({ isOpen, onClose, kidId, kidName, familyId, currentUserId }) => {
  const [activeTab, setActiveTab] = useState('queue');
  const [bookTab, setBookTab] = useState('regular'); // 'regular' or 'premium'
  const [books, setBooks] = useState([]);
  const [premiumBooks, setPremiumBooks] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [aiRecordings, setAiRecordings] = useState([]);
  const [readers, setReaders] = useState([]);
  const [queueItems, setQueueItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [personalRecordings, setPersonalRecordings] = useState([]);
  const [assignedPersonalRecordings, setAssignedPersonalRecordings] = useState(new Set());

  const [assignedRecordings, setAssignedRecordings] = useState(new Set());
  const [assignedAiRecordings, setAssignedAiRecordings] = useState(new Set());
  
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedReader, setSelectedReader] = useState(null);

  useEffect(() => {
    if (isOpen && kidId && familyId) {
      fetchData();
    }
  }, [isOpen, kidId, familyId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch regular books
      const booksRes = await fetch(`${API_URL}/api/books?per_page=100`, {
        credentials: 'include'
      });
      const booksData = await booksRes.json();
      setBooks(booksData.books || booksData);

      // Fetch premium books unlocked by current user
      try {
        const premiumRes = await fetch(`${API_URL}/api/reader/${currentUserId}/premium-unrecorded-books`, {
          credentials: 'include'
        });
        if (premiumRes.ok) {
          const premiumData = await premiumRes.json();
          setPremiumBooks(premiumData);
        }
      } catch (error) {
        console.log('Premium books not available:', error);
        setPremiumBooks([]);
      }

      const personalRecordingsRes = await fetch(`${API_URL}/api/family/${familyId}/all-personal-recordings`, {
        credentials: 'include'
      });
      let personalRecordings = [];
      if (personalRecordingsRes.ok) {
        const personalData = await personalRecordingsRes.json();
        setPersonalRecordings(personalData);
      }

      const readersRes = await fetch(`${API_URL}/api/family/${familyId}/readers`, {
        credentials: 'include'
      });
      const readersData = await readersRes.json();
      setReaders(readersData);

      const recordingsRes = await fetch(`${API_URL}/api/family/${familyId}/all-recordings`, {
        credentials: 'include'
      });
      const recordingsData = await recordingsRes.json();
      setRecordings(recordingsData);

      try {
        const aiRecordingsRes = await fetch(`${API_URL}/api/family/${familyId}/all-ai-recordings`, {
          credentials: 'include'
        });
        if (aiRecordingsRes.ok) {
          const aiRecordingsData = await aiRecordingsRes.json();
          setAiRecordings(aiRecordingsData);
        }
      } catch (error) {
        console.log('AI recordings not available:', error);
        setAiRecordings([]);
      }

      const queueRes = await fetch(`${API_URL}/api/queue/kid/${kidId}`, {
        credentials: 'include'
      });
      const queueData = await queueRes.json();
      setQueueItems(queueData);

      const assignedRes = await fetch(`${API_URL}/api/recording/kid/${kidId}`, {
        credentials: 'include'
      });
      const assignedData = await assignedRes.json();
      
      const videoIds = new Set(
        assignedData.recordings
          .filter(r => !r.is_ai)
          .map(r => r.id)
      );
      const aiIds = new Set(
        assignedData.recordings
          .filter(r => r.is_ai)
          .map(r => parseInt(r.id.replace('ai_', '')))
      );
      
      setAssignedRecordings(videoIds);
      setAssignedAiRecordings(aiIds);

    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToQueue = async () => {
    if (!selectedBook || !selectedReader) {
      alert('Please select both a book and a reader');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/queue/request-story`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          book_id: selectedBook,
          kid_id: kidId,
          reader_id: selectedReader,
          type: 'queue',
          is_premium: bookTab === 'premium'
        })
      });

      if (response.ok) {
        alert('Story request sent to reader!');
        setSelectedBook(null);
        setSelectedReader(null);
        fetchData();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to add to queue');
      }
    } catch (error) {
      console.error('Error adding to queue:', error);
      alert('Failed to add to queue');
    }
  };

  const handleAssignRecording = async (recordingId, isAi = false, isPersonal = false) => {
    try {
      let endpoint;
      let body;
      
      if (isPersonal) {
        const actualId = recordingId.toString().replace('personal_', '');
        endpoint = `${API_URL}/api/personal-books/recording/${actualId}/assign`;
        body = JSON.stringify({ kid_ids: [kidId] });
      } else if (isAi) {
        endpoint = `${API_URL}/api/voice/${recordingId}/assign-to-kid`;
        body = JSON.stringify({ kid_id: kidId });
      } else {
        endpoint = `${API_URL}/api/recording/${recordingId}/assign`;
        body = JSON.stringify({ kid_ids: [kidId] });
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: body
      });

      if (response.ok) {
        alert(`${isPersonal ? 'Personal story' : isAi ? 'AI story' : 'Recording'} assigned successfully!`);
        fetchData();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to assign recording');
      }
    } catch (error) {
      console.error('Error assigning recording:', error);
      alert('Failed to assign recording');
    }
  };

  const handleRemoveFromQueue = async (queueId) => {
    try {
      const response = await fetch(`${API_URL}/api/queue/${queueId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        alert('Removed from queue');
        fetchData();
      }
    } catch (error) {
      console.error('Error removing from queue:', error);
      alert('Failed to remove from queue');
    }
  };

  if (!isOpen) return null;

  const allRecordings = [
    ...recordings.map(r => ({ ...r, is_ai: false, is_personal: false, type: 'video' })),
    ...aiRecordings.map(r => ({ ...r, is_ai: true, is_personal: false, type: 'audio' })),
    ...personalRecordings.map(r => ({ 
      ...r, 
      is_ai: false, 
      is_personal: true, 
      type: 'personal',
      id: `personal_${r.id}`
    }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const currentBooks = bookTab === 'regular' ? books : premiumBooks;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '1rem',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
            Add Stories for {kidName}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              color: '#6b7280'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Main Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          padding: '0 1.5rem'
        }}>
          <button
            onClick={() => setActiveTab('queue')}
            style={{
              padding: '1rem 1.5rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'queue' ? 'bold' : 'normal',
              color: activeTab === 'queue' ? '#db2777' : '#6b7280',
              borderBottom: activeTab === 'queue' ? '3px solid #db2777' : '3px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            Request New Story
          </button>
          <button
            onClick={() => setActiveTab('recordings')}
            style={{
              padding: '1rem 1.5rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'recordings' ? 'bold' : 'normal',
              color: activeTab === 'recordings' ? '#db2777' : '#6b7280',
              borderBottom: activeTab === 'recordings' ? '3px solid #db2777' : '3px solid transparent',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Assign Existing Stories
            <span style={{
              background: '#db2777',
              color: 'white',
              borderRadius: '1rem',
              padding: '0.125rem 0.5rem',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}>
              {allRecordings.length}
            </span>
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '1.5rem'
        }}>
          {loading ? (
            <p style={{ textAlign: 'center', color: '#6b7280' }}>Loading...</p>
          ) : activeTab === 'queue' ? (
            <div>
              {/* Current Queue */}
              {queueItems.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>
                    Pending Requests ({queueItems.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {queueItems.map(item => (
                      <div key={item.id} style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: item.is_premium ? '#fffbeb' : '#fef3c7',
                        borderColor: item.is_premium ? '#fbbf24' : '#e5e7eb'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                            {item.is_premium && (
                              <Crown size={16} style={{ color: '#f59e0b' }} />
                            )}
                            <p style={{ fontWeight: '600', color: '#1f2937' }}>
                              {item.book?.title}
                            </p>
                          </div>
                          <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            Waiting for: {item.reader?.name}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveFromQueue(item.id)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add to Queue Form */}
              <div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#1f2937' }}>
                  Request New Story
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                  Select a book and reader to request a new recording
                </p>
                
                {/* Book Selection with Tabs */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    Select Book
                  </label>
                  
                  {/* Book Type Tabs */}
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '1rem',
                    borderBottom: '2px solid #e5e7eb'
                  }}>
                    <button
                      onClick={() => {
                        setBookTab('regular');
                        setSelectedBook(null);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontWeight: bookTab === 'regular' ? '600' : '400',
                        color: bookTab === 'regular' ? '#db2777' : '#6b7280',
                        borderBottom: bookTab === 'regular' ? '3px solid #db2777' : '3px solid transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      <Book size={16} />
                      Regular ({books.length})
                    </button>
                    <button
                      onClick={() => {
                        setBookTab('premium');
                        setSelectedBook(null);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontWeight: bookTab === 'premium' ? '600' : '400',
                        color: bookTab === 'premium' ? '#f59e0b' : '#6b7280',
                        borderBottom: bookTab === 'premium' ? '3px solid #fbbf24' : '3px solid transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      <Crown size={16} />
                      Premium ({premiumBooks.length})
                    </button>
                  </div>

                  {/* Books Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: '1rem',
                    maxHeight: '300px',
                    overflow: 'auto',
                    padding: '0.5rem'
                  }}>
                    {currentBooks.length === 0 ? (
                      <p style={{ 
                        gridColumn: '1 / -1', 
                        textAlign: 'center', 
                        color: '#6b7280',
                        padding: '2rem'
                      }}>
                        {bookTab === 'premium' 
                          ? 'No premium books unlocked yet'
                          : 'No regular books available'
                        }
                      </p>
                    ) : (
                      currentBooks.map(book => (
                        <div
                          key={book.id}
                          onClick={() => setSelectedBook(book.id)}
                          style={{
                            border: selectedBook === book.id 
                              ? (bookTab === 'premium' ? '3px solid #fbbf24' : '3px solid #db2777')
                              : '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            cursor: 'pointer',
                            textAlign: 'center',
                            background: selectedBook === book.id 
                              ? (bookTab === 'premium' ? '#fffbeb' : '#fce7f3')
                              : 'white',
                            transition: 'all 0.2s',
                            height: 'fit-content',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            position: 'relative'
                          }}
                        >
                          {bookTab === 'premium' && (
                            <div style={{
                              position: 'absolute',
                              top: '0.5rem',
                              right: '0.5rem',
                              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                              color: 'white',
                              padding: '0.125rem 0.375rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.5rem',
                              fontWeight: '700',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.125rem'
                            }}>
                              <Crown size={8} />
                            </div>
                          )}
                          
                          <div>
                            {book.pdf_url ? (
                              <div style={{
                                width: '100%',
                                height: '150px',
                                marginBottom: '0.75rem',
                                border: bookTab === 'premium' ? '1px solid #fbbf24' : '1px solid #e5e7eb',
                                borderRadius: '0.5rem',
                                overflow: 'hidden',
                                backgroundColor: bookTab === 'premium' ? '#fffbeb' : '#f9fafb'
                              }}>
                                <iframe
                                  src={`${book.pdf_url.trim()}#page=1&view=FitH`}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                    pointerEvents: 'none'
                                  }}
                                  title={`${book.title} preview`}
                                />
                              </div>
                            ) : (
                              <div style={{
                                fontSize: '2rem',
                                marginBottom: '0.75rem',
                                color: bookTab === 'premium' ? '#f59e0b' : '#db2777'
                              }}>
                                {bookTab === 'premium' ? '👑' : '📖'}
                              </div>
                            )}
                            <p style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem', lineHeight: '1.2' }}>
                              {book.title}
                            </p>
                            <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              by {book.author}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Reader Selection */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    Select Reader
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {readers.length === 0 ? (
                      <p style={{ textAlign: 'center', color: '#6b7280', padding: '1rem' }}>
                        No readers in family yet. Invite family members to be readers!
                      </p>
                    ) : (
                      readers.map(reader => (
                        <div
                          key={reader.id}
                          onClick={() => setSelectedReader(reader.id)}
                          style={{
                            border: selectedReader === reader.id ? '3px solid #059669' : '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            background: selectedReader === reader.id ? '#d1fae5' : 'white',
                            transition: 'all 0.2s'
                          }}
                        >
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: '#059669',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold'
                          }}>
                            {reader.first_name?.[0]}
                          </div>
                          <div>
                            <p style={{ fontWeight: '600' }}>
                              {reader.first_name} {reader.last_name}
                            </p>
                          </div>
                          {selectedReader === reader.id && <Check size={20} style={{ marginLeft: 'auto', color: '#059669' }} />}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Add Button */}
                <button
                  onClick={handleAddToQueue}
                  disabled={!selectedBook || !selectedReader}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: selectedBook && selectedReader 
                      ? (bookTab === 'premium' ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' : '#db2777')
                      : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: 'bold',
                    cursor: selectedBook && selectedReader ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    fontSize: '1rem'
                  }}
                >
                  <Plus size={20} /> Request {bookTab === 'premium' ? 'Premium ' : ''}Story Recording
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.25rem' }}>
                  Available Stories ({allRecordings.length})
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  {recordings.length} video · {aiRecordings.length} AI audio
                </p>
              </div>
              
              {allRecordings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <p style={{ color: '#6b7280', marginBottom: '0.5rem', fontSize: '1rem' }}>
                    No recordings available yet
                  </p>
                  <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                    Request stories from readers first!
                  </p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: '1rem'
                }}>
                  {allRecordings.map(recording => {
                    const isAi = recording.is_ai;
                    const isPersonal = recording.is_personal;
                    const isAssigned = isPersonal 
                      ? assignedPersonalRecordings.has(recording.id.toString().replace('personal_', ''))
                      : isAi 
                      ? assignedAiRecordings.has(recording.id)
                      : assignedRecordings.has(recording.id);
                    
                    return (
                      <div
                        key={`${isPersonal ? 'personal' : isAi ? 'ai' : 'video'}-${recording.id}`}
                        style={{
                          border: isPersonal ? '2px solid #10b981' : '1px solid #e5e7eb',
                          borderRadius: '0.75rem',
                          padding: '1rem',
                          background: isAssigned 
                            ? '#f3f4f6' 
                            : isPersonal 
                            ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                            : isAi 
                            ? 'linear-gradient(135deg, #faf5ff 0%, #ffffff 100%)' 
                            : 'white',
                          opacity: isAssigned ? 0.6 : 1,
                          position: 'relative'
                        }}
                      >
                        {/* Personal Badge */}
                        {isPersonal && (
                          <div style={{
                            position: 'absolute',
                            top: '0.75rem',
                            right: '0.75rem',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.625rem',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            📚 PERSONAL
                          </div>
                        )}
                        
                        {/* AI Badge */}
                        {isAi && !isPersonal && (
                          <div style={{
                            position: 'absolute',
                            top: '0.75rem',
                            right: '0.75rem',
                            background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.625rem',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            🤖 AI
                          </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                          {isPersonal ? (
                            <BookOpen size={24} style={{ color: '#10b981', flexShrink: 0 }} />
                          ) : isAi ? (
                            <Volume2 size={24} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                          ) : (
                            <Video size={24} style={{ color: '#db2777', flexShrink: 0 }} />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ 
                              fontWeight: '600', 
                              fontSize: '0.875rem', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              color: isPersonal ? '#064e3b' : '#1f2937'
                            }}>
                              {recording.book_title || recording.book?.title || 'Untitled'}
                            </p>
                            <p style={{ 
                              fontSize: '0.75rem', 
                              color: isPersonal ? '#047857' : '#6b7280' 
                            }}>
                              by {recording.book_author || recording.book?.author || 'Unknown'}
                            </p>
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <User size={16} style={{ color: '#6b7280' }} />
                          <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {isPersonal 
                              ? `Personal by ${recording.reader_name}`
                              : isAi 
                              ? `AI: ${recording.voice_name || 'Unknown'}` 
                              : `Read by ${recording.reader_name}`
                            }
                          </p>
                        </div>

                        {isAi && (
                          <p style={{ fontSize: '0.625rem', color: '#8b5cf6', marginBottom: '0.5rem', fontWeight: '600' }}>
                            Original: {recording.reader_name}
                          </p>
                        )}
                        
                        {isPersonal && recording.recording_type && (
                          <p style={{ fontSize: '0.75rem', color: '#065f46', marginBottom: '0.5rem' }}>
                            Type: {recording.recording_type === 'audio_only' ? '🎙️ Audio' : '📹 Video'}
                          </p>
                        )}
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                          <Calendar size={16} style={{ color: '#6b7280' }} />
                          <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {new Date(recording.created_at).toLocaleDateString()}
                          </p>
                        </div>

                        {isAssigned ? (
                          <div style={{
                            padding: '0.5rem',
                            background: '#059669',
                            color: 'white',
                            borderRadius: '0.5rem',
                            textAlign: 'center',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                          }}>
                            <Check size={16} /> Assigned
                          </div>
                        ) : (
                          <button
                            onClick={() => handleAssignRecording(recording.id, isAi, isPersonal)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              background: isPersonal 
                                ? 'linear-gradient(135deg, #10b981, #059669)'
                                : isAi 
                                ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' 
                                : '#db2777',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.5rem',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '0.875rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <Plus size={16} /> Assign to {kidName}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};